/* 16215cbc: print-file preparation after acceptance — Bambu Studio CLI
   slicing jobs run by the provider from the order page, plus per-printer
   flattened slicer profiles edited on the printer card. */

export interface OrderModelFile {
  modelFileId: string;
  fileName: string;
  format: string;
  /** Plates inside the 3MF project; null/absent = unknown or not a 3MF (61454143). */
  plateCount?: number | null;
}

export interface SlicingPrinter {
  printerId: string;
  name: string;
  brand: string;
  model: string;
  isActive: boolean;
  hasProfiles: boolean;
  /** Bundled BBL baseline family usable when the printer has no own profiles (61454143). */
  bundledProfileFamily?: string | null;
}

export interface SlicingContext {
  orderId: string;
  canCreateJobs: boolean;
  modelFiles: OrderModelFile[];
  printers: SlicingPrinter[];
  /** 1a6b6f55: false when the host has no slicer CLI — the page shows an honest
   * notice instead of dead queue buttons (raw model download still works). */
  slicerAvailable: boolean;
}

/** API serializes enums as numbers; normalized to the string union locally. */
export type SlicingJobStatus = 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Timeout';

export interface SlicingResultInfo {
  estimatedPrintMinutes?: number;
  totalFilamentGrams?: number;
  layerCount?: number;
  /** 'printer' | 'bundled:<family>' | 'embedded' (61454143). */
  profileSource?: string | null;
}

export interface SlicingJob {
  jobId: string;
  orderId: string;
  printerId: string;
  printerName: string;
  modelFileId: string;
  modelFileName: string;
  status: SlicingJobStatus | number;
  errorMessage?: string | null;
  result?: SlicingResultInfo | null;
  hasArtifact: boolean;
  /** Plate picked for the job; 0 = all plates (61454143). */
  plateIndex?: number;
  createdAtUtc: string;
  completedAtUtc?: string | null;
  /** 1a6b6f55: 'gcode' (default) = ready-to-print .gcode.3mf; '3mf' = project
   * 3MF with baked settings, no slicing. Older rows serialize as 'gcode'. */
  exportKind?: 'gcode' | '3mf';
}

export interface PrinterSlicingProfiles {
  printerId: string;
  printerName: string;
  machineProfileJson?: string | null;
  processProfileJson?: string | null;
  filamentProfileJson?: string | null;
}

export const SLICING_JOB_STATUSES: SlicingJobStatus[] = [
  'Pending', 'Running', 'Completed', 'Failed', 'Timeout',
];

export function normalizeSlicingJobStatus(status: SlicingJobStatus | number): SlicingJobStatus {
  if (typeof status === 'string') return status;
  return SLICING_JOB_STATUSES[status] ?? 'Failed';
}

export const SLICING_STATUS_LABELS: Record<SlicingJobStatus, string> = {
  Pending: 'В очереди',
  Running: 'Подготовка',
  Completed: 'Готово',
  Failed: 'Ошибка',
  Timeout: 'Таймаут',
};

export function slicingStatusBadgeClass(status: SlicingJobStatus): string {
  switch (status) {
    case 'Completed': return 'badge badge-success';
    case 'Failed':
    case 'Timeout': return 'badge badge-danger';
    case 'Running': return 'badge badge-warning';
    default: return 'badge badge-neutral';
  }
}
